import React from 'react'
import * as PropTypes from 'prop-types'
import {createPopper} from '@popperjs/core'
import ReactDOM from 'react-dom'
import {version} from '../../../package.json'
import memorize from '../../lib/momorize'
import Option from './option'
import TagList from './tag-list'
import Search from './search'
import useKeyPress from '@hooks/useKeyPress'

let id = 0
const MAX_OPTION_LENGTH = 7

const uniqueId = () => {
  const PREFIX = `${version}${Math.ceil(Math.random() * 9999 + 1000).toString()}`
  return `${PREFIX}${++id}`
}

const defaultIsEqual = (a, b) => a.key === b.key

const defaultFilter = (keyword, option) => {
  if (option.title) {
    return option.members?.some(member => member.toLowerCase().includes(keyword.toLowerCase()))
  }
  if (typeof option.text !== 'string') return true

  return option.text.toLowerCase().includes(keyword.toLowerCase())
}

const isSelectable = item =>
  // Ignore type: header divider and disabled ones
  item && !item.disabled && !item.type

const findNextSelectableOption = (options, start) => {
  for (let i = start; i < options.length; i++) {
    if (isSelectable(options[i])) return i
  }

  return null
}

const findPrevSelectableOption = (options, start) => {
  for (let i = start; i >= 0; i -= 1) {
    if (isSelectable(options[i])) return i
  }

  return null
}

const defaultHighlight = (keyword, option) => {
  if (typeof option.text !== 'string') return option.text
  return option.text
}

const DEFAULT_LOADING = (
  <div className="tw:flex is-justify-content-center">
    <i className="fa-solid fa-spinner fa-spin" />
  </div>
)

const defaultIsValidNewOption = (keyword, options) =>
  options.every(i => (typeof i.text === 'string' ? i.text.toLowerCase() : i.text) !== keyword.toLowerCase())

const CREATABLE_SELECT_KEY = uniqueId()

const Select = ({
  isEqual = defaultIsEqual,
  filter = defaultFilter,
  isValidNewOption = defaultIsValidNewOption,
  highlight = defaultHighlight,
  width = 300,
  isMultiple = false,
  isClearable = false,
  isLoading = false,
  optionHeight = 30.25,
  maxOptionsLength = MAX_OPTION_LENGTH,
  isSelectionStateRemember = true,
  className = '',
  triggerAppendOptions = () => null,
  customRenderAllOptionsSelected,
  hiddenSelectionOptionKeys = [],
  multipleSelectAllOption,
  isFieldHeightFlexible = false,
  minDropdownWidth,
  controlName,
  value,
  onChange,
  disabled,
  keyword,
  onKeywordChange,
  options,
  placeholder,
  notFoundContent,
  popupWidth,
  customRenderValue,
  customRenderOptionContent,
  isCreatable,
  onCreate,
  onDropdownOpenStateChange,
  customRenderCreateContent,
  isGrouping,
}) => {
  const [state, setState] = React.useState({
    keyword: keyword || '',
    value,
    open: false,
    activeIndex: null,
    prevOptions: options,
    creating: false,
  })

  React.useEffect(() => {
    const nextState = {
      ...state,
      value,
    }
    setState(nextState)
  }, [value])

  const [itemHeight, setItemHeight] = React.useState(0)

  const popperRef = React.useRef()
  const triggerRef = React.useRef()
  const popupRef = React.useRef()

  const searchResultRef = React.useRef(null)

  React.useEffect(() => {
    setItemHeight(state.open ? optionHeight : 0)
    if (state.open) {
      if (!popperRef.current) {
        popperRef.current = createPopper(triggerRef.current, popupRef.current, {
          placement: 'bottom',
          modifiers: [
            {
              name: 'sameWidth',
              enabled: true,
              fn: ({state}) => {
                state.styles.popper.width = `${
                  minDropdownWidth && state.rects.reference.width < minDropdownWidth
                    ? state.rects.reference.width + 180
                    : state.rects.reference.width
                }px`
              },
              phase: 'beforeWrite',
              requires: ['computeStyles'],
              effect: ({state}) => {
                state.elements.popper.style.width = `${state.elements.reference.clientWidth}px`
              },
            },
          ],
        })
      }
    } else if (popperRef.current) {
      popperRef.current.destroy()
      popperRef.current = null
    }

    if (onDropdownOpenStateChange) onDropdownOpenStateChange(state.open)

    return () => {
      if (popperRef.current) popperRef.current.destroy()
    }
  }, [state.open])

  const inputRef = React.createRef()

  const resetKeyword = keyword => {
    if (keyword === undefined) keyword = ''

    if (onKeywordChange) {
      onKeywordChange(keyword)
    } else {
      setState({
        ...state,
        keyword,
      })
    }
  }

  const onSelect = item => {
    if (!item || item.disabled || item.type || disabled) {
      return
    }

    if (item.key === CREATABLE_SELECT_KEY) {
      onCreateClick()
      return
    }

    if (isMultiple === false) {
      setState({
        ...state,
        open: false,
        activeIndex: null,
        keyword: '',
        value: isSelectionStateRemember ? item : null,
      })
      if (onChange) {
        onChange(item)
      }
    } else {
      const {value} = state
      const valueIndex = value.findIndex(it => isEqual(it, item))
      focusSearchInput()
      let nextValue = valueIndex >= 0 ? value.filter((_it, index) => index !== valueIndex) : [...value, item]

      if (multipleSelectAllOption) {
        if (item.key === multipleSelectAllOption?.key) {
          if (nextValue.length !== options.length) {
            nextValue = options
          } else {
            nextValue = []
          }
        } else {
          nextValue = nextValue.filter(v => v.key !== multipleSelectAllOption?.key)
        }
      }

      setState({
        ...state,
        open: false,
        activeIndex: null,
        keyword: '',
        value: isSelectionStateRemember ? nextValue : null,
      })

      if (onChange) {
        onChange(
          nextValue.filter(v => v.key !== multipleSelectAllOption?.key),
          item
        )
      }
    }
  }

  const onRemove = item => {
    if (disabled) {
      return
    }

    const {value} = state
    const nextValue = value.filter(it => !isEqual(item, it))
    focusSearchInput()
    setState({
      ...state,
      value: nextValue,
    })

    if (onChange) {
      onChange(nextValue)
    }
  }
  const downKeyPressed = useKeyPress('ArrowDown')
  const upKeyPressed = useKeyPress('ArrowUp')

  React.useEffect(() => {
    if (downKeyPressed) {
      const currentActiveIndex = state.activeIndex

      const currentOption = options?.find((_, index) => index === currentActiveIndex)

      if (currentOption && currentOption.metadata?.isDisabled) {
        setState({
          ...state,
          activeIndex: currentActiveIndex === null ? 0 : currentActiveIndex + 1,
        })
      }
    }
  }, [downKeyPressed])

  React.useEffect(() => {
    if (upKeyPressed) {
      const currentActiveIndex = state.activeIndex

      const currentOption = options?.find((_, index) => index === currentActiveIndex)

      if (currentOption && currentOption.metadata?.isDisabled) {
        setState({
          ...state,
          activeIndex: currentActiveIndex === null ? options.length - 1 : currentActiveIndex - 1,
        })
      }
    }
  }, [upKeyPressed])

  const onOptionMouseEnter = index => {
    if (disabled) {
      return
    }

    setState({
      ...state,
      activeIndex: index,
    })
  }

  const onOptionMouseLeave = index => {
    if (disabled) {
      return
    }

    setState({
      ...state,
      activeIndex: null,
    })
  }

  const selectCurrentIndex = () => {
    if (disabled) {
      return
    }

    const {activeIndex} = state

    if (activeIndex !== null) {
      const _options = filterOptions(isCreatable, options, filter, state.keyword, isValidNewOption)
      onSelect(_options[activeIndex])
    }
  }

  const renderOption = (option, index) => {
    const {value, activeIndex, creating} = state
    let isSelected = value && (isMultiple ? value.findIndex(it => isEqual(it, option)) >= 0 : isEqual(value, option))

    let optionContent = null
    let isLoading = false
    if (option.key === CREATABLE_SELECT_KEY) {
      isLoading = creating
      if (customRenderCreateContent) optionContent = customRenderCreateContent(option)
      else optionContent = <span className="is-highlighted">{`Create ${option.text}`}</span>
    } else if (customRenderOptionContent) {
      optionContent = customRenderOptionContent(option)
    } else {
      const keyword = state.keyword.trim()
      optionContent = filter !== false && keyword.length > 0 ? highlight(keyword, option) : option.text
    }

    const isSelectAllOption = option.key === multipleSelectAllOption?.key

    if (isSelectAllOption) {
      isSelected = value.length === options.filter(o => o.key !== multipleSelectAllOption.key).length
    }

    return (
      <Option
        key={option.key}
        value={option}
        isSelected={isSelected}
        active={index === activeIndex}
        index={index}
        isMultiple={isMultiple}
        isLoading={isLoading}
        onMouseEnter={onOptionMouseEnter}
        onMouseLeave={onOptionMouseLeave}
        onSelect={onSelect}
        metadata={option.metadata}
      >
        {optionContent}
      </Option>
    )
  }

  const handleKeywordChange = e => {
    if (disabled) return

    resetKeyword(e.target.value)
  }

  const onIndexChange = delta => {
    if (disabled) {
      return
    }

    const _options = filterOptions(isCreatable, options, filter, state.keyword, isValidNewOption)

    let nextIndex
    if (state.activeIndex === null) {
      if (delta < 0) {
        nextIndex = _options.length - 1
      } else {
        nextIndex = 0
      }
    } else {
      nextIndex = (state.activeIndex + delta) % _options.length
    }

    if (nextIndex >= _options.length) {
      nextIndex = _options.length - 1
    }

    if (nextIndex < 0) {
      nextIndex = 0
    }

    if (!isSelectable(_options[nextIndex])) {
      let enabled
      if (delta > 0) {
        enabled = findNextSelectableOption(_options, nextIndex)
      } else {
        enabled = findPrevSelectableOption(_options, nextIndex)
      }

      if (!enabled) {
        nextIndex = null
      }

      nextIndex = enabled
    }

    if (state.activeIndex === nextIndex) {
      nextIndex = null
    }

    // Scroll to option
    if (typeof state.activeIndex === 'number' && state.activeIndex >= 0) {
      searchResultRef.current.scrollTop =
        (state.activeIndex + (delta < 0 ? 1 : 2)) * itemHeight - maxOptionsLength * itemHeight
    }

    setState({
      ...state,
      activeIndex: nextIndex,
    })
  }

  const renderValue = () => {
    if (isMultiple) {
      const {value} = state
      if (value?.length > 0) {
        if (
          value.length === options.filter(v => v.key !== multipleSelectAllOption?.key).length &&
          customRenderAllOptionsSelected
        )
          return customRenderAllOptionsSelected(value)
        return renderTagList(value)
      }
    } else {
      const {value} = state

      if (value) {
        return customRenderValue ? (
          customRenderValue(value)
        ) : (
          <span className="tw:text-black ellipsis tw:pr-3">{value.text}</span>
        )
      }
    }

    return <span className="tw:text-gray-light tw:pr-2">{placeholder}</span>
  }

  const renderTagList = value => (
    <TagList
      list={value}
      renderValue={customRenderValue}
      isClearable={isClearable}
      onRemove={onRemove}
      hiddenSelectionOptionKeys={hiddenSelectionOptionKeys}
      isFieldHeightFlexible={isFieldHeightFlexible}
    />
  )

  const onCreateClick = () => {
    const {keyword} = state

    if (onCreate) {
      setState({...state, creating: true})

      onCreate(keyword.trim())
        .then(() => {
          if (isMultiple) {
            focusSearchInput()
          }
        })
        .finally(() => {
          setState({...state, creating: false, keyword: '', open: false})
        })
    }
  }

  const filterOptions = memorize((isCreatable, options, filter, keyword, isValidNewOption) => {
    const filtered = filter !== false && keyword ? options.filter(it => filter(keyword, it)) : options

    const pendingCreateOption =
      isCreatable && keyword && isValidNewOption(keyword, options)
        ? [
            {
              key: CREATABLE_SELECT_KEY,
              text: keyword,
              groupName: null,
            },
          ]
        : []

    var data = [...pendingCreateOption, ...filtered]

    if (isGrouping) {
      const groups = data.reduce((groups, item) => {
        const group = groups[item.groupName] || []
        group.push(item)
        groups[item.groupName] = group
        return groups
      }, {})

      data = []
      Object.keys(groups).forEach(function eachKey(key, i) {
        data.push({
          key: `title ${i}`,
          text: key,
          groupName: key,
          isGroupTitle: true,
        })
        groups[key].forEach(option => {
          data.push(option)
        })
      })
    }
    return data
  })

  const focusSearchInput = () => {
    inputRef?.current?.focus()
  }

  const renderSelectContent = () => {
    if (!state.open || disabled) return null
    const keyword = state.keyword.trim()

    if (isLoading) {
      return DEFAULT_LOADING
    }

    const filtered = filterOptions(isCreatable, options, filter, keyword, isValidNewOption)

    if (filtered?.length > 0) {
      return (
        <div>
          <div
            ref={searchResultRef}
            style={{
              width: '100%',
              height: filtered.length > maxOptionsLength ? `${maxOptionsLength * itemHeight}px` : 'auto',
              margin: '0 auto',
              overflow: 'auto',
            }}
          >
            <div style={{width: '100%', height: filtered.length * itemHeight + 10, minHeight: '200px'}}>
              {filtered.map((o, i) => renderOption(o, i))}
            </div>
          </div>
          {triggerAppendOptions &&
            triggerAppendOptions({
              keyword,
              onHideSelect: () => setState({...state, open: false}),
            })}
        </div>
      )
    }

    return (
      <>
        <div className="dropdown-item">
          {notFoundContent ? notFoundContent({context: state, setItem: item => onSelect(item)}) : 'No result'}
        </div>
        {triggerAppendOptions &&
          triggerAppendOptions({
            keyword,
            onHideSelect: () => setState({...state, open: false}),
          })}
      </>
    )
  }

  const getSearchPlaceholder = () => {
    if (isMultiple) {
      if (state.value && state.value.length > 0) {
        return ''
      }

      return placeholder
    }

    const value = state.value || null
    if (!value || typeof value.text !== 'string') {
      return placeholder
    }

    return value.text
  }

  const onClear = e => {
    e.stopPropagation()
    const {keyword} = state
    focusSearchInput()

    if (keyword) {
      resetKeyword()
      return
    }

    if (isMultiple) {
      const value = []
      setState({
        ...state,
        value,
      })
      if (onChange) {
        onChange(value)
      }
    } else {
      const value = null
      setState({
        ...state,
        value,
      })
      if (onChange) {
        onChange(value)
      }
    }
  }

  const notEmpty = isMultiple ? Array.isArray(state.value) && state.value.length > 0 : state.value
  const showClear = isClearable && !disabled && notEmpty

  return (
    <div
      className={`dropdown ${state.open ? 'is-active' : ''}`}
      tabIndex={disabled ? undefined : 0}
      style={{width: popupWidth ?? width, maxWidth: popupWidth ?? width}}
      onKeyDown={e => {
        if (!disabled) {
          switch (e.key) {
            case 'ArrowDown': {
              if (!state.open) {
                setState({
                  ...state,
                  open: true,
                })
                focusSearchInput()
              }

              break
            }

            default:
              break
          }
        }
      }}
    >
      <div className="dropdown-trigger" style={{width, display: 'inline-grid'}}>
        <button
          ref={triggerRef}
          disabled={disabled}
          data-cy={`rselect-${controlName || uniqueId()}`}
          className={`button is-fullwidth is-outlined ${
            disabled ? 'tw:bg-background' : 'has-background-white'
          } tw:flex is-justify-content-space-between ${className}`}
          style={{overflow: 'hidden', height: isFieldHeightFlexible ? 'fit-content' : 'unset'}}
          type="button"
          onClick={() => {
            setState({
              ...state,
              open: !state.open,
            })
          }}
          id="rselect-impersonate"
        >
          {renderValue()}
          <div className="tw:flex is-align-items-center">
            {showClear && (
              <span className="tw:text-black help tw:px-1 tw:mt-0" onClick={onClear}>
                <strong>clear</strong>
              </span>
            )}

            <span className="icon is-small tw:block tw:text-black">
              <i className={`fa-solid fa-angle-${state.open ? 'up' : 'down'}`} />
            </span>
          </div>
        </button>
      </div>
      {state.open &&
        ReactDOM.createPortal(
          <div style={{width}} role="menu" className={`${state.open ? '' : 'is-hidden'} is-overlay`}>
            <div
              ref={popupRef}
              key={state.open}
              className="dropdown-content r-popper"
              style={{pointerEvents: 'auto', display: state.open ? 'auto' : 'none'}}
            >
              <Search
                ref={inputRef}
                controlName={controlName || uniqueId()}
                isOpen={state.open}
                placeholder={getSearchPlaceholder()}
                value={keyword}
                onChange={handleKeywordChange}
                onIndexChange={onIndexChange}
                onEnter={selectCurrentIndex}
                onLostFocus={() => setState({...state, open: false, keyword: ''})}
              />
              {state.open && renderSelectContent()}
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}

Select.propTypes = {
  className: PropTypes.string,
  controlName: PropTypes.string,
  onChange: PropTypes.func,
  onCreate: PropTypes.func,
  isEqual: PropTypes.func,
  filter: PropTypes.func,
  isValidNewOption: PropTypes.func,
  highlight: PropTypes.func,
  appendOptionsLength: PropTypes.number,
  triggerAppendOptions: PropTypes.func,
  width: PropTypes.oneOfType([
    PropTypes.number, // For example could be 300 (px)
    PropTypes.string, // For example could be "100%"
  ]),
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  isSelectionStateRemember: PropTypes.bool,
  placeholder: PropTypes.string,
  isCreatable: PropTypes.bool,
  isMultiple: PropTypes.bool,
  isClearable: PropTypes.bool,
  isLoading: PropTypes.bool,
  optionHeight: PropTypes.number,
  maxOptionsLength: PropTypes.number,
  isGrouping: PropTypes.bool,
  groupBy: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    })
  ),
  customRenderAllOptionsSelected: PropTypes.func,
  hiddenSelectionOptionKeys: PropTypes.array,
  multipleSelectAllOption: PropTypes.oneOfType([
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
    () => null,
  ]),
  isFieldHeightFlexible: PropTypes.bool,
}

export default Select
